import { deepMix } from '@antv/util';
import { ItemState } from 'ui/workflows/flowchart/constants';
import { NodeData, Shape, ShapeSize } from 'ui/workflows/flowchart/types';
import { DEFAULT_NODE_SIZE, groupIds, GroupName, shapeNames } from '../../../constants';
import { drawNode } from '../../base/baseUtils';
import { DrawFooterDetailsOptions } from '../../baseWithFooter/baseWithFooter.types';
import {
  calculateNodeWithFooterHeight,
  drawFooter,
  hasFooterText,
} from '../../baseWithFooter/baseWithFooter.utils';
import { getChildNodeDimensions } from './positions';
import { setChildNodeState } from './state';

const childNodeGroupId = 'child-node-group';

const createChildNodeName = (prefix: string, type: string) => `childNode:${prefix}-${type}`;
const createGroupId = (type: string) => createChildNodeName(childNodeGroupId, type);

/**
 * Draws the content underneath the divider, which includes all the child nodes
 */
export const drawNestedNodeContent = (args: DrawFooterDetailsOptions) => {
  const { group, footerGroup, options, model } = args;
  const { childNodes = [] } = model?.nodeData as NodeData;

  if (!childNodes?.length || !footerGroup) return;

  const { width: childWidth, y: initialY, childPadding, x } = getChildNodeDimensions(args);
  const [offsetTop] = childPadding;
  let prevWrapper: Shape;

  childNodes.forEach((childNode, index) => {
    const childOptions = deepMix({}, options, childNode.options);
    const groupId = createGroupId(childNode.type);
    const childGroup = footerGroup.addGroup({
      id: groupId,
      name: GroupName.ChildNode,
      capture: true,
      visible: true,
      index,
      options: childOptions,
      groupNodeType: childNode.type,
      states: [],
    });
    const y = (prevWrapper?.getBBox?.()?.maxY ?? initialY) + offsetTop;
    const drawArgs = {
      size: [childWidth, DEFAULT_NODE_SIZE[1]] as ShapeSize,
      options: childOptions,
      group: childGroup,
      model: childNode,
      x,
      y,
    };

    prevWrapper = drawNode(drawArgs);

    if (hasFooterText(childNode)) {
      const footerGroupId = `${groupId}:${groupIds.footer}`;
      const dividerShapeName = `${footerGroupId}:${shapeNames.divider}`;
      const footer = drawFooter({ ...drawArgs, groupId: footerGroupId, dividerShapeName });
      const height = calculateNodeWithFooterHeight({ footer, startY: y, options: childOptions });
      prevWrapper.attr('height', height);
    }

    if (childNode.isSelected) {
      setChildNodeState(childGroup, ItemState.Selected);
    }
  });

  group.sort();
};
